import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, KeyRound } from 'lucide-react';
import { NousVizMark } from '@/components/brand/NousVizMark';
import { Button } from '@/components/ui/Button';
import { useAuthConfig, googleLoginUrl, useMe } from '@/api/auth';
import { setToken } from '@/lib/authToken';
import { apiFetch } from '@/api/client';
import { toast } from '@/components/ui/Toaster';

export function LoginRoute() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const errorMessage = params.get('m');
  const { data: config, isLoading: configLoading } = useAuthConfig();
  const { data: me, isLoading: meLoading } = useMe();

  // Already logged in? Bounce home.
  useEffect(() => {
    if (me && me.status === 'active') {
      nav('/', { replace: true });
    }
  }, [me, nav]);

  return (
    <div className="min-h-screen bg-bg flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center mb-6">
          <NousVizMark className="h-5 text-fg" />
        </div>

        <div className="bg-bg-elevated border border-border rounded-lg shadow-pop p-6">
          <div className="text-center mb-5">
            <h1 className="text-md font-semibold text-fg">Operator console</h1>
            <p className="text-xs text-fg-muted mt-1">
              Sign in to manage NousViz Cloud workspaces.
            </p>
          </div>

          {errorMessage && (
            <div className="mb-4 rounded-md border border-[color:var(--danger)]/40 bg-[color:var(--danger-soft)]/50 px-3 py-2 flex items-start gap-2 text-xs text-fg">
              <AlertCircle size={13} className="text-[color:var(--danger)] mt-0.5 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {configLoading || meLoading ? (
            <div className="text-xs text-fg-muted text-center py-4">Loading…</div>
          ) : config?.google_oauth_enabled ? (
            <>
              <Button
                variant="primary"
                className="w-full justify-center h-9"
                onClick={() => { window.location.href = googleLoginUrl(); }}
              >
                <GoogleIcon /> Sign in with Google
              </Button>
              {config.allowed_domains.length > 0 && (
                <p className="mt-3 text-2xs text-fg-subtle text-center">
                  Restricted to{' '}
                  {config.allowed_domains.map(d => (
                    <span key={d} className="font-mono">@{d}</span>
                  )).reduce<React.ReactNode[]>((acc, el, i) => i === 0 ? [el] : [...acc, ', ', el], [])}
                  .
                </p>
              )}
            </>
          ) : (
            <OperatorTokenSignIn />
          )}
        </div>

        <div className="mt-4 text-center text-2xs text-fg-subtle">
          NousViz Cloud · admin.nousviz.cloud
        </div>
      </div>
    </div>
  );
}

function OperatorTokenSignIn() {
  const nav = useNavigate();
  const [token, setTokenInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = token.trim();
    if (!trimmed) return;
    setSubmitting(true);
    // Stash token first so apiFetch picks it up, then probe /auth/me to
    // verify the backend accepts it.
    setToken(trimmed);
    try {
      await apiFetch('/auth/me');
      toast.info('Signed in', 'Using shared operator token. Google SSO will replace this once Workspace is set up.');
      nav('/', { replace: true });
    } catch {
      setToken('');
      setError('Backend rejected that token. Check ADMIN_BEARER_TOKEN in backend/.env.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="rounded-md border border-border bg-surface-2/40 px-3 py-2.5 text-2xs text-fg-muted leading-relaxed">
        <div className="font-semibold text-fg mb-0.5 inline-flex items-center gap-1.5">
          <KeyRound size={11} /> Operator token sign-in
        </div>
        Google SSO isn't configured yet — sign in with the shared operator token from <code className="font-mono">backend/.env</code> (<code className="font-mono">ADMIN_BEARER_TOKEN</code>). When SSO is wired, this falls away automatically.
      </div>

      <input
        type="password"
        value={token}
        onChange={e => setTokenInput(e.target.value)}
        autoFocus
        placeholder="Operator token"
        className="w-full h-9 px-3 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm font-mono"
      />

      {error && (
        <div className="rounded-md border border-[color:var(--danger)]/40 bg-[color:var(--danger-soft)]/50 px-3 py-2 text-2xs text-fg flex items-start gap-2">
          <AlertCircle size={12} className="text-[color:var(--danger)] mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      <Button
        type="submit"
        variant="primary"
        className="w-full justify-center h-9"
        disabled={!token.trim() || submitting}
      >
        {submitting ? 'Verifying…' : 'Continue'}
      </Button>
    </form>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" className="mr-2" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}
